"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/theme-toggle";

const nav = [
  { href: "/dashboard", label: "Home" },
  { href: "/protected-sites", label: "My sites" },
  { href: "/targets", label: "Suspects" },
  { href: "/detections", label: "Scans" },
  { href: "/evidence", label: "Evidence" },
  { href: "/cases", label: "Cases" },
  { href: "/reports", label: "Reports" },
];

const more = [
  { href: "/providers", label: "Providers" },
  { href: "/submissions", label: "Submissions" },
  { href: "/settings", label: "Settings" },
];

export function AppShell({
  children,
  unread,
}: {
  children: React.ReactNode;
  unread: number;
}) {
  const pathname = usePathname();

  return (
    <div className="min-h-screen text-[var(--text)]">
      <header className="sticky top-0 z-40 border-b border-[var(--border)] bg-[color-mix(in_srgb,var(--bg)_78%,transparent)] backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-3 md:px-6">
          <Link href="/dashboard" className="flex shrink-0 items-center gap-2">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-[var(--accent)] text-sm font-semibold text-[var(--accent-ink)] shadow-[0_10px_24px_var(--shadow)]">
              M
            </span>
            <span className="serif text-lg">MirrorReport</span>
          </Link>
          <nav className="hidden min-w-0 flex-1 items-center gap-1 lg:flex">
            {nav.map((item) => {
              const active =
                item.href === "/dashboard"
                  ? pathname === "/dashboard"
                  : pathname === item.href || pathname.startsWith(`${item.href}/`);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    "shrink-0 rounded-full px-2.5 py-1.5 text-[13px] text-[var(--text-muted)]",
                    active && "bg-[var(--bg-hover)] text-[var(--text)]",
                  )}
                >
                  {item.label}
                </Link>
              );
            })}
            <details className="relative">
              <summary className="cursor-pointer list-none rounded-full px-2.5 py-1.5 text-[13px] text-[var(--text-muted)] [&::-webkit-details-marker]:hidden">
                More
              </summary>
              <div className="panel-3d absolute left-0 z-50 mt-2 w-44 p-2">
                {more.map((item) => (
                  <Link key={item.href} href={item.href} className="block rounded-lg px-3 py-2 text-sm hover:bg-[var(--bg-hover)]">
                    {item.label}
                  </Link>
                ))}
              </div>
            </details>
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <details className="relative lg:hidden">
              <summary className="btn-3d list-none bg-[var(--bg-card)] px-3 py-2 text-sm [&::-webkit-details-marker]:hidden">
                Menu
              </summary>
              <nav className="panel-3d absolute right-0 z-50 mt-2 w-52 p-2">
                {[...nav, ...more, { href: "/activity", label: "Activity" }].map((item) => (
                  <Link key={item.href} href={item.href} className="block rounded-lg px-3 py-2 text-sm hover:bg-[var(--bg-hover)]">
                    {item.label}
                  </Link>
                ))}
              </nav>
            </details>
            <ThemeToggle />
            <Link href="/activity" className="btn-3d bg-[var(--bg-card)] px-3 py-2 text-sm">
              Activity{unread > 0 ? ` ${unread}` : ""}
            </Link>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-12">{children}</main>
    </div>
  );
}
